import { getStroke } from 'perfect-freehand';
import { POINT_STRIDE, isHighlighter, profileOf, type PenProfile, type Stroke } from 'shared';

type PointTriple = [number, number, number];

export function pointsToTriples(points: Float32Array | number[], from = 0): PointTriple[] {
  const out: PointTriple[] = [];
  for (let i = from * POINT_STRIDE; i < points.length; i += POINT_STRIDE) {
    out.push([points[i] as number, points[i + 1] as number, points[i + 2] as number]);
  }
  return out;
}

/**
 * Ponta chanfrada: numa caligráfica a espessura não vem da pressão, vem do
 * ângulo entre o movimento e o corte da pena — por isso o traço engrossa numa
 * diagonal e some na outra. Como o perfect-freehand só entende pressão, o
 * ângulo é convertido em pressão aqui.
 */
function applyNib(points: PointTriple[], nibAngle: number): PointTriple[] {
  if (points.length < 2) return points;
  return points.map((p, i) => {
    const prev = points[Math.max(0, i - 1)] as PointTriple;
    const next = points[Math.min(points.length - 1, i + 1)] as PointTriple;
    const dx = next[0] - prev[0];
    const dy = next[1] - prev[1];
    if (dx === 0 && dy === 0) return p;
    const dir = Math.atan2(dy, dx);
    const width = Math.abs(Math.sin(dir - nibAngle));
    return [p[0], p[1], 0.15 + 0.85 * width];
  });
}

export function buildPathFor(
  profile: PenProfile,
  points: PointTriple[],
  size: number,
  last: boolean,
): Path2D {
  const pts = profile.nibAngle !== undefined ? applyNib(points, profile.nibAngle) : points;
  const outline = getStroke(pts, {
    size,
    thinning: profile.thinning,
    smoothing: profile.smoothing,
    streamline: profile.streamline,
    start: { taper: profile.taperStart, cap: profile.taperStart === 0 },
    end: { taper: profile.taperEnd, cap: profile.taperEnd === 0 },
    simulatePressure: false,
    last,
  }) as number[][];

  const path = new Path2D();
  if (outline.length < 2) return path;
  const first = outline[0] as number[];
  path.moveTo(first[0] as number, first[1] as number);
  for (let i = 0; i < outline.length; i++) {
    const a = outline[i] as number[];
    const b = outline[(i + 1) % outline.length] as number[];
    path.quadraticCurveTo(
      a[0] as number, a[1] as number,
      ((a[0] as number) + (b[0] as number)) / 2,
      ((a[1] as number) + (b[1] as number)) / 2,
    );
  }
  path.closePath();
  return path;
}

const cache = new Map<string, Path2D>();

export function pathForStroke(s: Stroke): Path2D {
  const hit = cache.get(s.id);
  if (hit) return hit;
  const path = buildPathFor(profileOf(s.tool), pointsToTriples(s.points), s.size, true);
  cache.set(s.id, path);
  return path;
}

export function forgetStroke(id: string): void {
  cache.delete(id);
}

export function clearPathCache(): void {
  cache.clear();
}

/** Granulado do lápis: pontinhos de baixa opacidade ao longo do traço. */
function paintGrain(ctx: CanvasRenderingContext2D, s: Stroke): void {
  const p = s.points;
  ctx.save();
  ctx.fillStyle = s.color;
  ctx.globalAlpha = 0.16 * (s.opacity ?? 1);
  // Semente vinda do id: o granulado precisa ser igual toda vez que a página
  // é redesenhada, senão o lápis "ferve" a cada pan.
  let seed = 0;
  for (let i = 0; i < s.id.length; i++) seed = (seed * 31 + s.id.charCodeAt(i)) >>> 0;
  const rnd = (): number => ((seed = (seed * 1664525 + 1013904223) >>> 0) / 4294967296);
  for (let i = 0; i < p.length; i += POINT_STRIDE * 2) {
    const r = s.size * (0.4 + (p[i + 2] as number));
    for (let k = 0; k < 2; k++) {
      ctx.beginPath();
      ctx.arc(
        (p[i] as number) + (rnd() - 0.5) * r * 1.6,
        (p[i + 1] as number) + (rnd() - 0.5) * r * 1.6,
        s.size * 0.18, 0, Math.PI * 2,
      );
      ctx.fill();
    }
  }
  ctx.restore();
}

export function paintStroke(ctx: CanvasRenderingContext2D, s: Stroke, ghost = false): void {
  const profile = profileOf(s.tool);
  ctx.save();
  if (profile.behindInk) ctx.globalCompositeOperation = 'multiply';
  ctx.globalAlpha = ghost ? 1 : (s.opacity ?? profile.defaultOpacity);
  ctx.fillStyle = ghost ? '#C9CED6' : s.color;
  ctx.fill(pathForStroke(s));
  ctx.restore();
  if (profile.grain && !ghost) paintGrain(ctx, s);
}

/** Marca-texto vai por baixo da tinta (SPEC §7.3): grifar não pode encobrir. */
export function sortForPainting(strokes: Stroke[]): Stroke[] {
  return [...strokes].sort((a, b) => {
    const ah = isHighlighter(a.tool) ? 0 : 1;
    const bh = isHighlighter(b.tool) ? 0 : 1;
    if (ah !== bh) return ah - bh;
    return a.createdAt - b.createdAt || (a.id < b.id ? -1 : 1);
  });
}
